import { DataTable } from '@/features/list-views/components/data-table';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerViewFilters,
    ExplorerViewHandles,
    VisibilityWindow,
} from '@/server/utils/explorer-utils';
import type { ColumnDef, OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { forwardRef } from 'react';
import type { ExplorerTableData } from '../hooks/use-configurable-explorer-columns';
import { useExplorerTotalSummary } from '../hooks/use-explorer-total-summary';

interface ExplorerTableProps {
    data: ExplorerTableData[];
    columns: ColumnDef<ExplorerTableData>[];
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    isFetching: boolean;
    hasNextPage?: boolean;
    isLoading: boolean;
    filters: ExplorerViewFilters;
    window: VisibilityWindow;
    billingStatus: BillingStatus;
    customDateRange: CustomDateRange | null;
    rowSelection: RowSelectionState;
    setRowSelection: OnChangeFn<RowSelectionState>;
    sorting: SortingState;
    sortChangedInInterface: OnChangeFn<SortingState>;
}

export const ExplorerDataTable = forwardRef<ExplorerViewHandles, ExplorerTableProps>(
    (
        {
            data,
            columns,
            fetchNextPage,
            isFetchingNextPage,
            hasNextPage,
            isLoading,
            rowSelection,
            setRowSelection,
            sorting,
            sortChangedInInterface,
        },
        ref,
    ) => {
        const { summaryHeader } = useExplorerTotalSummary();

        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            onRowSelectionChange: setRowSelection,
            enableMultiRowSelection: true,
            onSortingChange: sortChangedInInterface,
            state: {
                rowSelection,
                sorting,
            },
        });

        return (
            <DataTable
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
                onFetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                table={table}
                columns={columns}
                summaryHeader={summaryHeader}
            />
        );
    },
);

ExplorerDataTable.displayName = 'ExplorerDataTable';
